import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material'
import type { FC } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import IcBaselineFlag from '~icons/ic/baseline-flag'
import IcBaselineSend from '~icons/ic/baseline-send'
import { RefType, ReportReason } from 'src/services/apollo/generated/graphql'
import { executeReport } from 'src/services/apollo/user'
import { track } from 'src/utils/tracker'
import { UIButton } from '../ui/Button'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
} from '../ui/Modal'

const REF_TYPE_I18N_KEY: Record<RefType, string> = {
  [RefType.Artwork]: 'artwork',
  [RefType.User]: 'user',
  [RefType.Model]: 'model',
  [RefType.Manga]: 'manga',
  [RefType.Post]: 'post',
  [RefType.Task]: 'task',
  [RefType.Media]: 'media',
  [RefType.Message]: 'message',
  [RefType.Coupon]: 'coupon',
  [RefType.Contest]: 'contest',
}

const REPORT_REASON_I18N_KEY: Record<ReportReason, string> = {
  [ReportReason.InappropriateContent]: 'inappropriate_content',
  [ReportReason.InappropriateProfile]: 'inappropriate_profile',
  [ReportReason.IrrelevantTag]: 'irrelevant_tag',
  [ReportReason.IrrelevantTitle]: 'irrelevant_title',
  [ReportReason.MislabeledArchivedArtwork]: 'mislabeled_archived_artwork',
  [ReportReason.MislabeledNsfw]: 'mislabeled_nsfw',
  [ReportReason.MistakenInfo]: 'mistaken_info',
  [ReportReason.NotAiArtwork]: 'not_ai_artwork',
  [ReportReason.NsfwPrompt]: 'nsfw_prompt',
  [ReportReason.Offensive]: 'offensive',
  [ReportReason.Other]: 'other',
  [ReportReason.RequestEditModel]: 'request_edit_model',
  [ReportReason.RuleViolations]: 'rule_violations',
  [ReportReason.SharingPersonalInformation]: 'sharing_personal_information',
  [ReportReason.Spam]: 'spam',
  [ReportReason.ClaimModel]: 'claim_model',
}

// Get available reasons based on refType
const getAvailableReasons = (refType: RefType): ReportReason[] => {
  switch (refType) {
    case RefType.Artwork:
      return [
        ReportReason.MistakenInfo,
        ReportReason.InappropriateContent,
        ReportReason.IrrelevantTag,
        ReportReason.IrrelevantTitle,
        ReportReason.Spam,
        ReportReason.NotAiArtwork,
        ReportReason.Offensive,
      ]
    case RefType.User:
      return [
        ReportReason.InappropriateProfile,
        ReportReason.InappropriateContent,
        ReportReason.Spam,
        ReportReason.SharingPersonalInformation,
        ReportReason.Offensive,
        ReportReason.RuleViolations,
        ReportReason.Other,
      ]
    case RefType.Model:
      return [
        ReportReason.MistakenInfo,
        ReportReason.InappropriateContent,
        ReportReason.IrrelevantTag,
        ReportReason.IrrelevantTitle,
        ReportReason.Spam,
        ReportReason.Offensive,
        ReportReason.Other,
      ]
    case RefType.Message:
      return [
        ReportReason.InappropriateContent,
        ReportReason.Spam,
        ReportReason.Offensive,
        ReportReason.SharingPersonalInformation,
        ReportReason.Other,
      ]
    default:
      return [ReportReason.InappropriateContent, ReportReason.Spam]
  }
}

export const ReportDialog: FC<{
  refId: string
  refType: RefType
  onComplete?: () => void
}> = ({ refId, refType, onComplete }) => {
  const { t } = useTranslation()

  const availableReasons = getAvailableReasons(refType)
  const [reason, setReason] = useState<ReportReason>(availableReasons[0])
  const [submitting, setSubmitting] = useState(false)

  const handleChange = (e: React.ChangeEvent<{ value: string }>) => {
    setReason(e.target.value as ReportReason)
  }

  const submit = async () => {
    setSubmitting(true)

    try {
      await executeReport({
        refType,
        refId,
        reason,
      })
      toast.success(t('artwork:report.success'))
      onComplete?.()
    } catch (e) {
      const err = e as Error
      toast.error(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <UIDialogHeader
        heading={
          <span className="inline-flex items-center gap-2">
            <IcBaselineFlag className="size-5" />
            <span>
              {t(`common:report.${REF_TYPE_I18N_KEY[refType]}.title`)}
            </span>
          </span>
        }
      />
      <UIDialogContent>
        <p>{t(`common:report.${REF_TYPE_I18N_KEY[refType]}.description`)}</p>
        <FormControl fullWidth className="mt-4">
          <RadioGroup value={reason} onChange={handleChange}>
            {availableReasons.map(reasonValue => (
              <FormControlLabel
                key={reasonValue}
                value={reasonValue}
                control={<Radio />}
                label={t(
                  `common:report.reasons.${REPORT_REASON_I18N_KEY[reasonValue]}`,
                )}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </UIDialogContent>
      <UIDialogFooter>
        <UIButton
          variant="filled"
          color="admin"
          onPress={submit}
          busy={submitting}
          icon={<IcBaselineSend className="mx-2 size-5" />}
        >
          <span>{t('artwork:action.report')}</span>
        </UIButton>
      </UIDialogFooter>
    </>
  )
}
export const dialogReport = async (refType: RefType, refId: string) => {
  track('report_click', {
    refType,
    refId,
  })
  return UIDialog.create({
    dismissable: true,
    children: ({ close }) => (
      <ReportDialog
        refType={refType}
        refId={refId}
        onComplete={() => {
          close()
        }}
      />
    ),
  })
}
